import React, { forwardRef } from "react"
import { Modal, ScrollView, TextStyle, TouchableOpacity, View, ViewStyle } from "react-native"

import Ionicons from "@expo/vector-icons/Ionicons"
import { ListItem } from "@rn-vui/themed"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { Text } from "./Ignite/Text"

interface BottomSheetListModalProps {
  heading: string
  items: string[]
  onItemPress: (item: string) => void
  selectedItem?: string
}

// Match the interface expected by @gorhom/bottom-sheet
interface BottomSheetModalMethods {
  present: () => void
  dismiss: () => void
  close: () => void
  snapToIndex: (index: number) => void
  snapToPosition: (position: string | number) => void
  expand: () => void
  collapse: () => void
  forceClose: () => void
}

export const BottomSheetListModal = forwardRef<BottomSheetModalMethods, BottomSheetListModalProps>(
  (props, ref) => {
    const { theme, themed } = useAppTheme()
    const safeInsets = useSafeAreaInsets()
    const [isVisible, setIsVisible] = React.useState(false)

    React.useImperativeHandle(ref, () => ({
      present: () => setIsVisible(true),
      dismiss: () => setIsVisible(false),
      close: () => setIsVisible(false),
      // Additional methods to match BottomSheetModalMethods interface
      snapToIndex: () => setIsVisible(true), // For web, just show the modal
      snapToPosition: () => setIsVisible(true), // For web, just show the modal
      expand: () => setIsVisible(true),
      collapse: () => setIsVisible(false),
      forceClose: () => setIsVisible(false),
    }))

    return (
      <Modal
        visible={isVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setIsVisible(false)}
      >
        <View style={themed($backdrop)}>
          <TouchableOpacity
            style={themed($backdropTouchable)}
            onPress={() => setIsVisible(false)}
            activeOpacity={1}
          />
          <View style={[themed($modalContainer), { backgroundColor: theme.colors.background }]}>
            <ScrollView
              contentContainerStyle={{
                paddingBottom: safeInsets.bottom + theme.spacing.md,
                paddingTop: theme.spacing.md,
              }}
            >
              <Text preset="heading" style={themed($heading)}>
                {props.heading}
              </Text>

              {props.items.map((item, index) => {
                return (
                  <ListItem
                    key={index}
                    onPress={() => {
                      props.onItemPress(item)
                      setIsVisible(false)
                    }}
                  >
                    <ListItem.Content>
                      <ListItem.Title>{item}</ListItem.Title>
                    </ListItem.Content>
                    {props.selectedItem === item && (
                      <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
                    )}
                  </ListItem>
                )
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    )
  },
)

BottomSheetListModal.displayName = "BottomSheetListModal"

const $backdrop: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  justifyContent: "flex-end",
})

const $backdropTouchable: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})

const $modalContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  maxHeight: "60%",
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  paddingHorizontal: spacing.md,
})

const $heading: ThemedStyle<TextStyle> = ({ colors }) => ({
  marginHorizontal: 16,
  marginBottom: 8,
  fontSize: 28,
  backgroundColor: colors.background,
})
